import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get('/auth/me');
        const userData = response.data.user || response.data;
        if (userData && userData.id) {
          setUser(userData);
        } else {
          throw new Error('Invalid user data');
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      setError(null);

      const response = await api.post('/auth/login', { email, password });
      const { token, user: userData } = response.data;

      if (!token || !userData) {
        throw new Error('Invalid response from server');
      }

      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);

      return { success: true, user: userData };
    } catch (err) {
      console.error('Login error:', err);
      const message = err.response?.data?.message || err.response?.data?.error || err.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (formData) => {
    try {
      setError(null);

      const response = await api.post('/auth/register', formData);
      const { token, user: newUser } = response.data;

      if (!token) {
        throw new Error('No token received');
      }

      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Load the FULL user (role, roles, permissions, matchmaker_type) so the
      // dashboard renders the correct backend after sign-up. The register
      // response only carries a minimal {id,email,...}. Falls back to that
      // minimal payload if /auth/me isn't reachable.
      try {
        const me = await api.get('/auth/me');
        setUser(me.data?.user || me.data || newUser || { email: formData.email });
      } catch (_) {
        setUser(newUser || { email: formData.email });
      }

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData);
      setUser(prev => ({ ...prev, ...response.data.user }));
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message || 'Update failed';
      return { success: false, error: message };
    }
  };

  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    const userRole = (user.role || '').toLowerCase();
    // user.roles is the multi-role array exposed by the backend; falls back
    // to just the primary role for older sessions / clients.
    const userRoles = Array.isArray(user.roles) ? user.roles.map(r => (r || '').toLowerCase()) : [];
    const allUserRoles = new Set([userRole, ...userRoles].filter(Boolean));
    return roleArray.some(role => allUserRoles.has(role.toLowerCase()));
  };

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    register,
    logout,
    updateProfile,
    hasPermission,
    hasRole,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
